/**
 * Wallet Authentication Hook
 * 
 * Provides wallet-specific checks for blockchain actions.
 * Supports two types of users:
 * 1. Anonymous wallet users: Can fund/join with just wallet connection
 * 2. Profile users: Need wallet linked to profile for creating projects/communities
 * 
 * Use Cases:
 * - Funding projects (anonymous OK)
 * - Joining communities (anonymous OK)
 * - Placing bids (anonymous OK)
 * - Creating projects (requires profile + linked wallet)
 * - Creating communities (requires profile + linked wallet)
 * 
 * @module hooks/useWalletAuth
 */

import { useCallback } from "react";
import { useAccount } from "wagmi";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import { useDispatch, useSelector } from "react-redux";
import { useGetUserQuery, useGetWalletStatusQuery } from "../redux/slices/api/userApi";
import {
  selectGeneral,
  setShowWalletRequiredModal,
  setShowAuthModal,
} from "../redux/slices/generalSlice";

/**
 * Hook for wallet checks in blockchain actions
 * 
 * @returns {Object} Wallet auth state and methods
 */
const useWalletAuth = () => {
  const dispatch = useDispatch();
  const { address, isConnected } = useAccount();
  const { openConnectModal } = useConnectModal();
  const { isLoggedIn, walletJustLinked } = useSelector(selectGeneral);

  // Get user data to check wallet linking status
  const { data: userData, isLoading: isUserLoading } = useGetUserQuery(undefined, {
    skip: !isLoggedIn,
  });

  // Get wallet status (more reliable than user data for this specific check)
  const { data: walletStatus, isLoading: isWalletStatusLoading } = useGetWalletStatusQuery(undefined, {
    skip: !isLoggedIn,
  });

  const user = userData?.user;
  
  // Check if wallet is linked to the account
  const hasLinkedWallet = !!(
    user?.public_address || 
    user?.wallet_linked || 
    walletStatus?.wallet_linked ||
    walletStatus?.wallet_connected
  );
  
  // Check if connected wallet matches the linked wallet
  const isWalletMatched = hasLinkedWallet && address && 
    user?.public_address?.toLowerCase() === address?.toLowerCase();

  /**
   * Ensure wallet is ready for SIMPLE blockchain transactions
   * (funding, joining communities, bidding)
   * 
   * Anonymous users can perform these actions with just wallet connection.
   * No profile or authentication required.
   * 
   * @param {string} action - Description of the action (for modal)
   * @returns {Object} { ready: boolean, reason?: string }
   */
  const ensureWalletReady = useCallback(async (action = "complete this action") => {
    // For simple actions, only check wallet connection
    // No authentication or profile required
    if (!isConnected) {
      return { ready: false, reason: "not_connected" };
    }

    // All good! Wallet connected, user can transact anonymously
    return { ready: true };
  }, [isConnected]);

  /**
   * Ensure wallet is ready for CREATOR actions
   * (creating projects, creating communities)
   * 
   * These actions require a user profile AND linked wallet.
   * 
   * @param {string} action - Description of the action (for modal)
   * @param {string} redirect - URL to redirect after setup
   * @returns {Object} { ready: boolean, reason?: string }
   */
  const ensureWalletReadyForCreator = useCallback(async (action = "complete this action", redirect = null) => {
    console.log("🔍 ensureWalletReadyForCreator check:", {
      isLoggedIn,
      hasLinkedWallet,
      isConnected,
      isWalletMatched,
      isLoading: isUserLoading || isWalletStatusLoading,
      action,
      redirect
    });

    // Wait for loading to complete before making decisions
    if (isUserLoading || isWalletStatusLoading) {
      console.log("⏳ Still loading user/wallet data, waiting...");
      return { ready: false, reason: "loading" };
    }

    // Step 1: Check if user is logged in (has profile)
    if (!isLoggedIn) {
      console.log("❌ User not logged in, showing auth modal");
      dispatch(setShowAuthModal({ show: true, redirect }));
      return { ready: false, reason: "not_logged_in" };
    }

    // Step 2: Check if wallet is linked to their profile
    if (!hasLinkedWallet) {
      console.log("❌ Wallet not linked, showing wallet required modal");
      // User has profile but needs to link wallet
      dispatch(setShowWalletRequiredModal({ 
        show: true, 
        action,
        redirect,
      }));
      return { ready: false, reason: "needs_linking" };
    }

    // Step 3: Check if wallet is connected
    if (!isConnected) {
      console.log("❌ Wallet not connected, opening connect modal");
      openConnectModal?.();
      return { ready: false, reason: "not_connected" };
    }

    // Step 4: Verify wallet matches (optional warning)
    if (hasLinkedWallet && !isWalletMatched) {
      console.warn("⚠️ Connected wallet differs from linked wallet", {
        connected: address,
        linked: user?.public_address
      });
    }

    // All good! User has profile and linked wallet
    console.log("✅ Wallet ready for creator actions!");
    return { ready: true };
  }, [
    isLoggedIn,
    hasLinkedWallet,
    isConnected,
    isWalletMatched,
    isUserLoading,
    isWalletStatusLoading,
    dispatch,
    openConnectModal,
    address,
    user?.public_address,
  ]);

  /**
   * Check if user can perform simple blockchain actions (without prompting)
   * Anonymous users with just wallet = true
   */
  const canTransact = isConnected;

  /**
   * Check if user can perform creator actions (without prompting)
   * Requires profile + linked wallet
   */
  const canCreate = isLoggedIn && isConnected && hasLinkedWallet;

  return {
    // State
    isConnected,
    isLoggedIn,
    hasLinkedWallet,
    isWalletMatched,
    canTransact, // Simple actions (fund, join)
    canCreate,   // Creator actions (create project/community)
    walletJustLinked,
    isLoading: isUserLoading || isWalletStatusLoading,
    address,
    linkedAddress: user?.public_address,

    // Methods
    ensureWalletReady,           // For simple actions (fund, join)
    ensureWalletReadyForCreator, // For creator actions (create)
    openConnectModal,
  };
};

export default useWalletAuth;
